import type {
  IPeerConnection,
  IPeerConnectionFactory,
  PeerConnectionOptions,
  TransceiverSpec,
} from './types';
import {
  WebRtcOptimizationManager,
  configFromMetadata,
  sdpTransformConfigFromOptimization,
} from './WebRtcOptimizationManager';
import { createSdpBitrateTransform } from './SdpBitrateTransform';
import { SdpInspector } from './SdpInspector';
import { IceHealthGuard } from './IceHealthGuard';
import { SdpGatherBuffer } from './SdpGatherBuffer';
import {
  GUEST_CONNECTION_STABLE_MS,
  GUEST_ICE_DISCONNECT_GRACE_MS,
  GUEST_ICE_EARLY_RESTART_MS,
  GUEST_PATH_SILENCE_MS,
  GUEST_RECONNECT_ATTEMPT_TIMEOUT_MS,
  GUEST_RECOVERY_DEADLINE_MS,
  GUEST_RECOVERY_HARD_CEILING_MS,
} from './webrtcLatencyContracts';
import { GuestRecoveryEpisode } from './guestRecoveryEpisode';
import type {
  BeaconE2eCongestionLevel,
  BeaconE2eSnapshot,
  PlaycastBeaconE2eApi,
} from './beaconWebrtcE2eContracts';
import { GuestCongestionMonitor } from './guestCongestionMonitor';
import { DecodeHintCollector, type RvfcVideoElement } from './decodeHintCollector';
import { isDebugWebrtcLoggingEnabled } from './debugLoggingFlag';
import {
  LATENCY_PROBE_MESSAGE_TYPE,
  isLatencyProbeMessage,
} from './latencyProbeContracts';
import { isInputPhotonTag, type InputPhotonSummary } from './inputPhotonContracts';
import { InputPhotonTracker } from './inputPhotonTracker';
import { FramePipelineSampler, formatLatencyCapabilityLine, latencySourceForProbe } from './framePipeline';
import { PresentationQualityTracker } from './streamQuality/presentationQualityTracker';
import { ReceiverLatencyHintController } from './receiverHints';
import { DrainRequestHandler } from './drain';
import type { GuestStatsSample } from './guestStreamStatsSampler';
import { REQUEST_KEYFRAME_MESSAGE } from './WebRtcOptimizationManager';
import { GuestPathSilenceWatch, isRecoverPathMessage } from './guestPathSilenceWatch';
import {
  decideAndHandleIceFailure,
  wireGuestSessionEvents,
} from './guestPeerSessionWiring';
import {
  attachGuestBeaconE2eHooks,
  detachGuestBeaconE2eHooks,
  buildGuestBeaconSnapshot,
  injectGuestCongestion,
  logRemoteCandidateCounts,
  recordGuestSdpBreadcrumb,
  clearTimer,
} from './guestPeerSessionBeacon';
import type {
  GuestPeerSessionOptions,
  EncodedFrameTap,
  GuestPeerSessionCallbacks,
  GuestRetryPolicy,
  GuestPeerLogger,
  GuestConnectOptions,
  ReconnectRelayOptions,
  RealtimeReadyGate,
} from './guestPeerSessionTypes';
import type { PeerMetadata } from '@playcastdotio/Messaging';
import { isDeliberateSessionEnd, isDisconnectRetryable } from './sessionEndContracts';
import { GuestStreamRecoveryBinding } from './integration/GuestStreamRecoveryBinding';
import { RtcSelectedPathReader } from './measurement/RtcSelectedPathReader';
import { SelectedNetworkPathObserver } from './networkHandoff/SelectedNetworkPathObserver';
import type { ChangeEventSource } from './networkHandoff/NetworkChangeSubscription';
import { SessionRecoveryActionArbiter } from './presentationRecovery/SessionRecoveryActionArbiter';
import type { RecoveryActionArbiter } from './streamRecovery/interfaces';
import type { FramePresentationEvidence } from './streamRecovery/contracts';
import { PeerMediaPolicyLifecycleManager, type PeerMediaPolicyLifecycle } from './mediaPolicy';
import type { SignalingGenerationV1 } from '@playcastdotio/Messaging';

/**
 * Terminal reason for a session that was hidden for longer than it can survive.
 *
 * Deliberately shaped to be RETRYABLE (`isDisconnectRetryable`): the transport is gone, but
 * the player is back at the screen, so the app must rejoin rather than show a dead end.
 */
const HIDDEN_SESSION_EXPIRED_REASON = 'session_hidden_timeout';

const VIEWER_RECVONLY_TRANSCEIVERS: TransceiverSpec[] = [
  { kind: 'audio', direction: 'recvonly' },
  { kind: 'video', direction: 'recvonly' },
];

/**
 * Shared guest/client PeerCore media session.
 * Awaits preferHardware (decode) before start(); applies SDP bitrate priming,
 * early receiver LL, keyframe until first decoded frame, and congestion limits.
 */
export class GuestPeerSession {
  private factory: IPeerConnectionFactory;
  private callbacks: GuestPeerSessionCallbacks;
  private retryPolicy: GuestRetryPolicy | undefined;
  private optimizationManager: WebRtcOptimizationManager;
  private readonly mediaPolicyLifecycle: PeerMediaPolicyLifecycle;
  private sdpInspector: SdpInspector;
  private iceHealth: typeof IceHealthGuard;
  private logger: GuestPeerLogger | undefined;

  private connection: IPeerConnection | null = null;
  private preWarmedConnection: IPeerConnection | null = null;
  private connected = false;
  private retryAttempt = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  /** Bounds a single reclaim attempt that never reaches ICE-connected. */
  private attemptWatchdogTimer: ReturnType<typeof setTimeout> | null = null;
  /** Refunds the attempt budget only once a recovered connection has held. */
  private stabilityTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * The one clock every recovery attempt is measured against, from transport loss until the
   * guest recovers or hands over to a refresh. Stops while the document is hidden.
   */
  private recovery: GuestRecoveryEpisode;
  private pendingCloseReason: string | null = null;
  private hostCloseInProgress = false;
  private closed = false;
  private connectionEpoch = 0;
  private disconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private lastHostPeerId: string | undefined;
  private lastIceServers: RTCIceServer[] | undefined;
  private lastMetadata: PeerMetadata | undefined;
  private gatherBuffer: SdpGatherBuffer | null = null;
  private preWarmCreatedAt = 0;
  private remoteDescriptionSet = false;
  private pendingRemoteIceCandidates: RTCIceCandidate[] = [];
  private iceDisconnectGraceTimer: ReturnType<typeof setTimeout> | null = null;
  private iceEarlyRestartTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Bitrate priming for the pre-warmed PC. Pre-warm runs before any metadata is known,
   * so the transform is late-bound: the PC is created with a stable indirection here and
   * connect() fills in the real transform once the profile is resolved.
   */
  private sdpBitrateTransform: ((sdp: string) => string) | undefined;
  private unwiredEpoch: (() => void) | null = null;
  private reconnectRelay: ReconnectRelayOptions;
  private realtimeReadyGate: RealtimeReadyGate | undefined;
  // Guards against stacking duplicate reclaim re-offers for a single failover.
  private reconnectInFlight = false;
  /** True while an in-place restartIce is in flight; full rebuild must not stack on it. */
  private iceRestartInFlight = false;
  /** In-place restart already issued because video stopped while ICE still said connected. */
  private pathRestartPending = false;
  /** framesDecoded when pathRestartPending was armed — clear only after this advances. */
  private pathRestartFramesAtArm: number | null = null;
  /** This connect is a reclaim and has not yet decoded a frame. */
  private reclaimAwaitingMedia = false;
  /**
   * Snapshot of `_hasDecodedFrame` taken right before a reclaim's `connect()` resets it.
   * Scopes the first-media watchdog to RECOVERY reclaims — a session that already showed
   * video before this drop. An initial connection (or a reclaim whose prior epoch never
   * decoded anything either) has no established video baseline to recover, and ICE/channel
   * connectivity holding past GUEST_CONNECTION_STABLE_MS is already a complete success
   * signal for that case — the same way it always has been.
   */
  private hadDecodedFrameBeforeReclaim = false;
  /** Bound a reclaim that connects but never receives a stats sample (getStats hung). */
  private firstMediaWatchdogTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly silenceWatch = new GuestPathSilenceWatch();
  private pathSilenceGapTimer: ReturnType<typeof setTimeout> | null = null;
  private pathRebuildTimer: ReturnType<typeof setTimeout> | null = null;
  private detachNetworkWatch: (() => void) | null = null;
  private congestion: GuestCongestionMonitor;
  private statsSampleListener: ((sample: GuestStatsSample) => void) | undefined;
  /** Decode-hint collector feeding frameDecodeHints onto clientStats. */
  private decodeHints: DecodeHintCollector;
  /**
   * Input-to-photon correlation. Unlike the calibrated glass-to-glass path this needs no
   * clock offset from the host, so it reports on any engine whose rVFC exposes an RTP
   * timestamp — and reports nothing at all on one that does not, rather than guessing.
   */
  private readonly inputPhoton = new InputPhotonTracker();
  private readonly framePipeline = new FramePipelineSampler();
  private readonly presentationQuality = new PresentationQualityTracker();
  private readonly recoveryBinding: GuestStreamRecoveryBinding;
  private readonly recoveryActionArbiter = new SessionRecoveryActionArbiter();
  private transportActionId: string | undefined;
  private transportActionSequence = 0;
  private remoteVideo: RvfcVideoElement | null = null;
  private readonly receiverHints: ReceiverLatencyHintController;
  /** App-provided receive-side encoded-frame tap; null when the app has none. */
  private readonly encodedFrameTap: EncodedFrameTap | null;
  /** Answers host standingLatencyDrain requests over the same channel they arrive on (Phase 5). */
  private readonly drainRequests: DrainRequestHandler;
  /** True once the host's latencyProbe ping has been answered over the data channel. */
  private latencyProbeHandshakeLive = false;
  /** Re-arms and sends one keyframe request. Published by the wiring for the live epoch. */
  private rearmKeyframes: (() => boolean) | null = null;
  private e2eHooks: PlaycastBeaconE2eApi | null = null;
  private _hasDecodedFrame = false;
  /** Host-offer generation echoed by this answer/ICE direction. */
  private remoteOfferGeneration: SignalingGenerationV1 | undefined;
  /** Once a v1 envelope is observed, generation-less signaling is no longer legacy-compatible. */
  private remoteGenerationRequired = false;

  private static readonly ICE_GATHER_TIMEOUT_MS = 500;
  private static readonly PREWARM_MAX_AGE_MS = 60_000;
  private static readonly ICE_DISCONNECT_GRACE_MS = GUEST_ICE_DISCONNECT_GRACE_MS;
  /**
   * How long ICE may sit in `disconnected` before an in-place restart is attempted, well
   * inside the disconnect grace. Most blips self-heal in under a second; for the rest, a
   * restart is a cheap re-offer that can recover the transport without tearing down media
   * or raising the reconnect cover — which the full reclaim at grace expiry cannot avoid.
   */
  private static readonly ICE_EARLY_RESTART_MS = GUEST_ICE_EARLY_RESTART_MS;
  /**
   * Default relay policy for a reconnect, computed at SESSION CONSTRUCTION (not class
   * load) so the platform the session actually runs on decides whether to force TURN.
   */
  private static defaultReconnectRelay(): ReconnectRelayOptions {
    return {
      // WebKit ignores `iceTransportPolicy: 'relay'` (its ICE does not implement the
      // enum), so a relay-forced reconnect there is identical to 'all' — and worse, it
      // abandons the cached server-reflexive candidates that 'all' would have kept for the
      // new NAT path. Safari reconnects keep the default policy; Chromium/Android get the
      // relay-forced rebuild.
      forceRelay: !GuestPeerSession.isWebKit(),
      transportPolicy: 'relay',
    };
  }

  private static isWebKit(): boolean {
    if (typeof navigator === 'undefined') return false;
    const userAgent = navigator.userAgent;
    return (
      userAgent.includes('Safari') &&
      !userAgent.includes('Chrome') &&
      !userAgent.includes('Chromium')
    );
  }

  constructor(options: GuestPeerSessionOptions) {
    this.factory = options.factory;
    this.callbacks = options.callbacks;
    this.retryPolicy = options.retryPolicy;
    this.optimizationManager = options.optimizationManager ?? new WebRtcOptimizationManager();
    this.sdpInspector = options.sdpInspector ?? new SdpInspector();
    this.iceHealth = options.iceHealth ?? IceHealthGuard;
    this.logger = options.logger;
    this.receiverHints = options.mediaPolicyLifecycle?.bundle.receiverPolicy
      ?? new ReceiverLatencyHintController();
    this.mediaPolicyLifecycle = options.mediaPolicyLifecycle ?? new PeerMediaPolicyLifecycleManager({
      optimizationManager: this.optimizationManager,
      receiverPolicy: this.receiverHints,
    });
    this.drainRequests = new DrainRequestHandler({
      controller: this.receiverHints,
      send: (json) => void this.connection?.send(json),
    });
    this.reconnectRelay = options.reconnectRelay ?? GuestPeerSession.defaultReconnectRelay();
    this.realtimeReadyGate = options.realtimeReadyGate;
          this.congestion = new GuestCongestionMonitor(
      {
        onFramesDecoded: () => this.markDecodedFrame(),
        onSample: (sample) => {
          this.noteInboundVideo(sample.framesDecoded, Date.now(), sample.decodeSample?.sourceId);
          this.statsSampleListener?.(sample);
        },
      },
      {
        isMobile: options.isMobile,
        capability: options.capability,
      },
    );
    this.decodeHints = new DecodeHintCollector({
      onCapability: (capability) => {
        if (isDebugWebrtcLoggingEnabled()) {
          // Once per session: what this engine's rVFC actually exposes and which latency source
          // the guest will label its samples with. The Phase 8 iPhone run reads this line.
          console.log(
            formatLatencyCapabilityLine(
              options.capability?.engine,
              capability,
              latencySourceForProbe(options.capability, capability, this.inputPhoton.summary().sampleCount > 0),
            ),
          );
        }
      },
      // Frame presentation is the "photon" end of the input-to-photon span. It rides the
      // collector's existing rVFC loop rather than registering a second one.
      onFrame: (observation) => {
        this.framePipeline.observe(observation);
        this.presentationQuality.observe(observation);
        if (typeof observation.rtpTimestamp !== 'number') return;
        const sample = this.inputPhoton.onFramePresented(
          observation.rtpTimestamp,
          observation.decodeWallMs,
        );
        if (sample && isDebugWebrtcLoggingEnabled()) {
          console.log('[InputPhoton]', sample);
        }
      },
    });
    this.decodeHints.start();
    this.congestion.setDecodeHintCollector(this.decodeHints);
    this.congestion.setInputPhotonSummaryProvider(() => this.inputPhoton.summary());
    this.congestion.setFramePipelineSampler(this.framePipeline);
    this.congestion.setPresentationSource({
      summarizeAndReset: () => this.presentationQuality.summarizeAndReset(this.decodeHints.playbackQuality()),
    });
    this.recoveryBinding = new GuestStreamRecoveryBinding({
      pathReader: new RtcSelectedPathReader(), pathObserver: new SelectedNetworkPathObserver(),
      presentation: this.presentationQuality, collector: this.decodeHints,
      visibility: options.visibilitySource, isMobile: options.isMobile,
      requestSample: () => this.congestion.requestSample(),
      eventSources: () => ({
        ice: this.connection?.getRtcPeerConnection()?.getReceivers?.().find((receiver) => receiver.track?.kind === 'video')?.transport?.iceTransport,
        network: typeof navigator === 'undefined' ? undefined :
          (navigator as Navigator & { connection?: ChangeEventSource }).connection,
      }),
      onPathChanged: (epoch) => {
        this.decodeHints.resetForPath(); this.framePipeline.reset(); this.inputPhoton.resetForNewConnection();
        // The selected path changed: the inbound counter belongs to a different stream, so a
        // stale baseline must not be read as silence on the new one.
        this.silenceWatch.resetForPath();
        this.pathRestartPending = false;
        this.pathRestartFramesAtArm = null;
        this.logger?.info?.('Selected network path changed', { epoch });
      },
    });
    this.congestion.setRecoveryBinding(this.recoveryBinding);
    this.congestion.setReceiverHintSource(this.receiverHints);
    this.encodedFrameTap = options.encodedFrameTap ?? null;
    this.congestion.setEncodedFrameSource(this.encodedFrameTap);
    this.recovery = new GuestRecoveryEpisode({
      onHiddenSessionExpired: () => this.handleHiddenSessionExpired(),
      visibilitySource: options.visibilitySource,
      logger: options.logger,
    });
    if (options.metadata) this.lastMetadata = options.metadata;
    if (options.enableBeaconE2eHooks !== false) {
      this.e2eHooks = attachGuestBeaconE2eHooks(this);
    }
    this.watchNetworkChanges();
  }

  /** True after first inbound video frame decoded. */
  get hasDecodedFrame(): boolean {
    return this._hasDecodedFrame;
  }

  /** Mark first decoded frame; stops further requestKeyframe sends. */
  markDecodedFrame(): void {
    if (this._hasDecodedFrame) return;
    this._hasDecodedFrame = true;
    this.reclaimAwaitingMedia = false;
    // The authoritative "a frame arrived" signal: clears the no-stats-sample bound even
    // when getStats() itself never calls back (CodeRabbit's exact ask — do not depend on
    // getStats()/notePollGap() to end the watchdog).
    this.firstMediaWatchdogTimer = clearTimer(this.firstMediaWatchdogTimer);
  }

  /**
   * Pre-create an RTCPeerConnection with iceCandidatePoolSize so STUN/TURN
   * binding starts immediately. Does not add transceivers (see connect).
   */
  preWarm(iceServers: RTCIceServer[]): void {
    this.destroyPreWarmedConnection();
    this.preWarmedConnection = this.factory.create({
      config: {
        iceServers,
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require',
        iceCandidatePoolSize: 10,
      },
      batchCandidates: true,
      batchCandidatesTimeout: 1000,
      enableDataChannels: true,
      channelLabel: 'data',
      sdpTransform: (sdp) => this.sdpBitrateTransform?.(sdp) ?? sdp,
    });
    this.preWarmCreatedAt = Date.now();
    this.logger?.info?.('Pre-warmed RTCPeerConnection with iceCandidatePoolSize=10');
  }

  hasPreWarmedConnection(): boolean {
    return this.preWarmedConnection !== null;
  }

  /**
   * Creates/activates the guest media session.
   * Awaits preferHardware (decode) before peer start() so offer/answer is primed.
   */
  async connect(
    hostPeerId: string,
    iceServers: RTCIceServer[],
    metadata: PeerMetadata,
    connectOptions?: GuestConnectOptions,
  ): Promise<void> {
    this.lastHostPeerId = hostPeerId;
    this.lastIceServers = iceServers;
    this.lastMetadata = metadata;
    this.congestion.setPeerId(hostPeerId);

    this.closed = false;
    // A rebuild the APP asked for is a fresh start, not a continuation: it gets a full
    // budget. A rebuild this session issued as a reclaim attempt is the same episode
    // continuing and must keep the clock — resetting there is what once made the ceiling
    // unreachable, because every attempt put elapsed time back to zero.
    //
    // The automatic post-exhaustion REFRESH (isRefresh=true) is the deliberate exception:
    // it is an app-initiated connect that must INHERIT the running episode so the ten-second
    // ceiling bounds the whole attempt, not just the in-session reclaim ladder. Without this,
    // a refresh would start a fresh episode and a bad link could spend four attempts and
    // twenty seconds instead of the intended two-plus-one ceiling.
    if (!this.reconnectInFlight && !connectOptions?.isRefresh) this.endRecoveryEpisode();
    this.cancelRetry();
    this.cancelAttemptTimers();
    this.cancelDisconnectTimer();
    this.cancelIceDisconnectGrace();
    this.cancelGatherBuffer();
    this.unwireEpoch();
    this.destroyConnection();
    this.pendingCloseReason = null;
    this.connectionEpoch++;
    // Retire the prior directional exchange before the replacement PC can receive ICE.
    // The capability bit remains sticky for this peer-session object.
    this.remoteOfferGeneration = undefined;
    this.remoteDescriptionSet = false;
    this.pendingRemoteIceCandidates = [];
    this._hasDecodedFrame = false;
    this.silenceWatch.reset();
    this.pathRestartPending = false;
    this.pathRestartFramesAtArm = null;
    this.firstMediaWatchdogTimer = clearTimer(this.firstMediaWatchdogTimer);
    this.latencyProbeHandshakeLive = false;
    this.congestion.setLatencyProbeHandshakeLive(false);
    this.congestion.resetForNewConnection();
    // RTP timestamps restart with the new SSRC, so any tag held from the old stream could
    // collide with an unrelated frame here.
    this.inputPhoton.resetForNewConnection();
    this.presentationQuality.reset();
    // Keep the collector wired across reconnects so rVFC RTP correlation stays available.
    this.congestion.setDecodeHintCollector(this.decodeHints);

    const optimizationConfig = configFromMetadata(metadata);
    // WebKit stays off the Chromium video playout path. The Opus rewrite is separate and
    // applies to Safari offers too (playoutDelay.ts).
    const sdpTransformConfig = sdpTransformConfigFromOptimization(optimizationConfig, undefined, {
      lowLatencyPlayout: !GuestPeerSession.isWebKit(),
      // Safari offers the same 20 ms Opus section the host answers with. Video playout
      // stays on the WebKit gate above.
      audioOpusPolicy: true,
    });
    const sdpTransform = sdpTransformConfig
      ? createSdpBitrateTransform(sdpTransformConfig)
      : undefined;
    // Publish before the connection is chosen so an adopted pre-warmed PC — whose
    // transform was installed at pre-warm time, before any profile existed — primes
    // its first offer with this peer's hints too.
    this.sdpBitrateTransform = sdpTransform;
    const epochAtConnect = this.connectionEpoch;

    const transportPolicy = connectOptions?.transportPolicy;

    let connection: IPeerConnection;
    // A pre-warmed PC was built with the default 'all' policy, so it cannot
    // honour a relay-forced reconnect — discard it and build fresh in that case.
    if (this.preWarmedConnection && !transportPolicy && this.isPreWarmHealthy()) {
      this.logger?.info?.('Using pre-warmed connection');
      connection = this.preWarmedConnection;
      this.preWarmedConnection = null;
    } else {
      if (this.preWarmedConnection) {
        this.logger?.warn?.('Discarding stale/unhealthy/relay-mismatched pre-warmed connection');
        this.destroyPreWarmedConnection();
      }
      if (transportPolicy) {
        this.logger?.info?.(`Reconnect: forcing iceTransportPolicy='${transportPolicy}' for new path`);
      }
      const options: PeerConnectionOptions = {
        id: hostPeerId,
        config: {
          iceServers,
          bundlePolicy: 'max-bundle',
          rtcpMuxPolicy: 'require',
          iceCandidatePoolSize: 10,
          ...(transportPolicy ? { iceTransportPolicy: transportPolicy } : {}),
        },
        batchCandidates: true,
        batchCandidatesTimeout: 1000,
        enableDataChannels: true,
        channelLabel: 'data',
        ...(sdpTransform ? { sdpTransform } : {}),
      };
      connection = this.factory.create(options);
    }

    this.connection = connection;
    connection.ensureTransceivers(VIEWER_RECVONLY_TRANSCEIVERS);

    const rtcPc = connection.getRtcPeerConnection();
    if (rtcPc) {
      this.mediaPolicyLifecycle.beginConnection(rtcPc, metadata);
      await this.mediaPolicyLifecycle.beforeNegotiation(rtcPc);
    }
    // A concurrent connect() can destroy this PC and swap in a winner while the
    // hardware probe is awaiting. Wiring the stale PC would overlay the live one.
    if (epochAtConnect !== this.connectionEpoch || this.connection !== connection) {
      this.logger?.warn?.('Ignoring connect applied to a superseded connection');
      return;
    }

    this.wireEvents(connection, optimizationConfig);
    connection.start({ polite: false });
  }

  async handleRemoteSdp(sdp: RTCSessionDescriptionInit, generation?: SignalingGenerationV1): Promise<void> {
    const generationAccepted = sdp.type === 'offer'
      ? this.acceptRemoteOfferGeneration(generation)
      : (!generation && this.remoteGenerationRequired
        ? false
        : this.mediaPolicyLifecycle.validateInboundGeneration(generation));
    if (!generationAccepted) {
      this.logger?.warn?.('Ignoring SDP from a stale signaling generation');
      return;
    }
    const connection = this.connection;
    if (!connection) {
      this.logger?.warn?.('No connection to signal SDP');
      return;
    }
    const epochAtSignal = this.connectionEpoch;
    await connection.signal(sdp);
    // A reconnect can swap in a new PC while signal() is awaiting. Marking the remote
    // description set would then drain this epoch's buffered candidates into the
    // replacement, which has no remote description yet.
    if (epochAtSignal !== this.connectionEpoch || this.connection !== connection) {
      this.logger?.warn?.('Ignoring remote SDP applied to a superseded connection');
      return;
    }
    this.remoteDescriptionSet = true;
    this.drainPendingRemoteIceCandidates();
    this.logRemoteCandidateCount(connection);
  }

  handleRemoteIceCandidate(candidate: RTCIceCandidate, generation?: SignalingGenerationV1): Promise<void> {
    const generationAccepted = this.remoteOfferGeneration
      ? this.matchesRemoteOfferGeneration(generation)
      : (!generation && this.remoteGenerationRequired
        ? false
        : this.mediaPolicyLifecycle.validateInboundGeneration(generation));
    if (!generationAccepted) {
      this.logger?.warn?.('Ignoring ICE from a stale signaling generation');
      return Promise.resolve();
    }
    if (!this.connection) {
      this.logger?.warn?.('No connection to add ICE candidate');
      return Promise.resolve();
    }
    if (!this.remoteDescriptionSet) {
      this.pendingRemoteIceCandidates.push(candidate);
      return Promise.resolve();
    }
    return this.connection.addIceCandidate(candidate);
  }

  send(data: string | ArrayBuffer, label?: string): boolean {
    if (!this.connection) return false;
    return this.connection.send(data, label);
  }

  /** Alias for dispose — closes the guest media session. */
  close(reason?: string): void {
    this.logger?.info?.(`Guest session close requested: ${reason ?? 'no reason given'}`);
    this.dispose();
  }

  dispose(): void {
    this.closed = true;
    this.congestion.getHealthRecorder().recordEvent('session-end', 'signaling');
    this.cancelRetry();
    this.cancelAttemptTimers();
    this.recovery.dispose();
    this.recoveryBinding.dispose();
    this.recoveryActionArbiter.reset();
    this.transportActionId = undefined;
    this.cancelDisconnectTimer();
    this.cancelIceDisconnectGrace();
    this.cancelGatherBuffer();
    this.detachNetworkWatch?.();
    this.detachNetworkWatch = null;
    this.unwireEpoch();
    this.destroyPreWarmedConnection();
    this.destroyConnection();
    if (this.e2eHooks) {
      detachGuestBeaconE2eHooks(this.e2eHooks);
      this.e2eHooks = null;
    }
    this.connected = false;
    this.remoteDescriptionSet = false;
    this.pendingRemoteIceCandidates = [];
    this._hasDecodedFrame = false;
  }

  isConnected(): boolean {
    return this.connected;
  }

  /** Current signaling identity for the live replacement RTCPeerConnection. */
  get signalingGeneration(): SignalingGenerationV1 {
    return { ...this.mediaPolicyLifecycle.bundle.generation };
  }

  private acceptRemoteOfferGeneration(generation?: SignalingGenerationV1): boolean {
    if (!generation && !this.remoteGenerationRequired) {
      this.remoteOfferGeneration = undefined;
      return true;
    }
    if (!generation) return false;
    if (generation.version !== 1 || !generation.sessionId.trim()
      || !Number.isInteger(generation.epoch) || generation.epoch < 0) return false;
    this.remoteGenerationRequired = true;
    const current = this.remoteOfferGeneration;
    if (current?.sessionId === generation.sessionId && generation.epoch < current.epoch) return false;
    this.remoteOfferGeneration = { ...generation };
    return true;
  }

  private matchesRemoteOfferGeneration(generation?: SignalingGenerationV1): boolean {
    const current = this.remoteOfferGeneration;
    return !!current && !!generation && generation.version === 1
      && generation.sessionId === current.sessionId && generation.epoch === current.epoch;
  }

  private generationForOutgoingSdp(sdp: RTCSessionDescriptionInit): SignalingGenerationV1 {
    if (sdp.type === 'offer') {
      this.remoteOfferGeneration = undefined;
      return this.signalingGeneration;
    }
    return this.remoteOfferGeneration ? { ...this.remoteOfferGeneration } : this.signalingGeneration;
  }

  private generationForOutgoingIce(): SignalingGenerationV1 {
    return this.remoteOfferGeneration ? { ...this.remoteOfferGeneration } : this.signalingGeneration;
  }

  /**
   * True while a transient reconnect / ICE recovery is still in flight.
   * Join UI must not show a failure CTA while this is true.
   */
  isReconnectInProgress(): boolean {
    if (this.closed) return false;
    return (
      this.reconnectInFlight ||
      this.iceRestartInFlight ||
      this.pathRestartPending ||
      this.pathRebuildTimer !== null ||
      this.pathSilenceGapTimer !== null ||
      this.firstMediaWatchdogTimer !== null ||
      this.retryTimer !== null ||
      this.attemptWatchdogTimer !== null ||
      this.iceDisconnectGraceTimer !== null ||
      this.disconnectTimer !== null
    );
  }

  getRetryAttempt(): number {
    return this.retryAttempt;
  }

  resetRetries(): void {
    this.retryAttempt = 0;
  }

  /**
   * Milliseconds left in the current recovery episode; 0 once spent, the full budget when no
   * episode is running.
   *
   * Published so the app layers can honour the SAME clock rather than each starting a fresh
   * one on top. Bounding every layer separately is what turned an unrecoverable transport
   * into roughly 33 seconds of frozen frame.
   */
  recoveryBudgetRemainingMs(): number {
    return this.recovery.remainingMs();
  }

  /**
   * Resume latency of the last completed recovery (drop-start → media-resumed), for the
   * health record and the harness resume-latency SLO. Undefined until a resume completes.
   */
  getLastResumeLatencyMs(): number | undefined {
    return this.recovery.lastResumeLatencyMs();
  }

  /**
   * End the current episode so the next recovery is timed from its own start.
   *
   * A refresh — tearing the stack down and rebuilding it the way a fresh join does — is a
   * new episode, not a continuation of the one that failed, so it gets a full budget. The
   * two deliberately do not sum: the app decides to spend a second budget, it does not
   * inherit a spent one.
   *
   * Also resurrects the episode if it was disposed (e.g. by close()). The session instance
   * is reused for the page lifetime, so an explicit rejoin that calls close() must restore
   * the episode rather than leave it permanently dead.
   */
  resetRecoveryEpisode(): void {
    this.recovery.resurrect();
    this.endRecoveryEpisode();
  }

  injectCongestion(level: BeaconE2eCongestionLevel): void {
    injectGuestCongestion(this.connection, this.congestion, level);
  }

  /** Most recent guest stats sample, for app-layer telemetry. */
  getLastStatsSample(): GuestStatsSample | undefined {
    return this.congestion.getLastSample();
  }

  getPresentationEvidence(): FramePresentationEvidence | undefined {
    return this.remoteVideo ? this.recoveryBinding.presentationEvidence() : undefined;
  }

  getRecoveryActionArbiter(): RecoveryActionArbiter { return this.recoveryActionArbiter; }

  /** Subscribe to every stats sample. Replaces any previous listener. */
  onStatsSample(listener: ((sample: GuestStatsSample) => void) | undefined): void {
    this.statsSampleListener = listener;
  }

  /**
   * Attach the remote `<video>` so DecodeHintCollector can read rVFC media RTP timestamps
   * for calibrated latency reports (`source: 'rvfc-rtp'`). Idempotent; pass null to detach.
   */
  attachRemoteVideo(video: RvfcVideoElement | null): void {
    if (this.remoteVideo === video) return;
    this.remoteVideo = video;
    this.recoveryBinding.attachVideo(video);
    this.decodeHints.attach(video);
    // A new element restarts its presentedFrames / playback-quality counters.
    this.presentationQuality.reset();
  }

  /** The receiver-hint controller (reassert / pulse are the Phase 5 drain levers). */
  public receiverHintController(): ReceiverLatencyHintController {
    return this.receiverHints;
  }

  /** Consume a host standingLatencyDrain request; the result is sent back on the datachannel. */
  public handleDrainRequest(data: string): boolean {
    return this.drainRequests.handle(data);
  }

  /**
   * Handle a host latencyProbe message over the data channel.
   *
   * On a ping the guest answers immediately with a pong carrying its local wall time - the
   * host needs that to estimate the clock offset before any report can be calibrated. Once
   * answered, the handshake is live: calibrated reports replace frameDecodeHints on the
   * wire, but the collector stays attached so reports can peek rVFC RTP timestamps.
   */
  /**
   * Claim a sequence number for an input about to be sent, or null when this input is
   * inside the sampling interval and should go out untagged.
   */
  sampleInputForPhoton(nowMs: number = Date.now()): number | null {
    return this.inputPhoton.sampleInput(nowMs);
  }

  /** Rolling input-to-photon latency for telemetry and the debug overlay. */
  inputPhotonSummary(): InputPhotonSummary {
    return this.inputPhoton.summary();
  }

  /** Consume the host's "frame R answers input N" tag. */
  handleInputPhotonMessage(data: string): boolean {
    let parsed: unknown;
    try {
      parsed = JSON.parse(data);
    } catch {
      return false;
    }
    if (!isInputPhotonTag(parsed)) return false;
    this.inputPhoton.acceptTag(parsed);
    return true;
  }

  handleLatencyProbeMessage(data: string): boolean {
    let parsed: unknown;
    try {
      parsed = JSON.parse(data);
    } catch {
      return false;
    }
    if (!isLatencyProbeMessage(parsed)) return false;
    if (parsed.role !== 'ping') return true; // pongs/reports are host-side traffic

    const pong = {
      type: LATENCY_PROBE_MESSAGE_TYPE,
      role: 'pong' as const,
      probeId: parsed.probeId,
      hostWallMs: parsed.hostWallMs,
      guestWallMs: Date.now(),
    };
    const sent = this.connection?.send(JSON.stringify(pong)) ?? false;
    if (sent) {
      // The handshake is live: calibrated reports start. Keep the collector attached.
      this.latencyProbeHandshakeLive = true;
      this.congestion.setLatencyProbeHandshakeLive(true);
    }
    return true;
  }

  /** True once the host's latencyProbe ping has been answered over the data channel. */
  get isLatencyProbeHandshakeLive(): boolean {
    return this.latencyProbeHandshakeLive;
  }

  /**
   * Ask the host for a keyframe.
   *
   * Re-arms the startup keyframe latch first: without that, the first decoded frame silences
   * this path for the rest of the session, so a mid-session freeze had no client-side
   * recovery lever at all. The host independently rate-limits client keyframe requests, so a
   * refused request is harmless.
   */
  requestKeyframe(): boolean {
    if (this.closed || !this.connection) return false;
    // Prefer the wired path: it applies the same readiness guards (remote video present,
    // data channel open) as the startup requests.
    if (this.rearmKeyframes) return this.rearmKeyframes();
    return this.connection.send(JSON.stringify(REQUEST_KEYFRAME_MESSAGE));
  }

  /** Trigger an in-place ICE restart. Resolves without throwing when there is no connection. */
  async restartIce(): Promise<void> {
    const connection = this.connection;
    if (this.closed || !connection || !this.beginTransportAction()) return;
    const actionId = this.transportActionId;
    const epoch = this.connectionEpoch;
    try {
      await connection.restartIce();
    } catch (error) {
      this.logger?.error?.('ICE restart failed', { error: String(error) });
      if (this.connection === connection && this.connectionEpoch === epoch && this.transportActionId === actionId) {
        if (actionId) this.recoveryActionArbiter.finish(actionId, 'failed');
        this.transportActionId = undefined;
      }
    }
  }

  getBeaconE2eSnapshot(): BeaconE2eSnapshot {
    return buildGuestBeaconSnapshot(this.congestion);
  }

  private wireEvents(
    connection: IPeerConnection,
    optimizationConfig: ReturnType<typeof configFromMetadata>,
  ): void {
    this.unwireEpoch();
    this.unwiredEpoch = wireGuestSessionEvents(
      connection,
      optimizationConfig,
      this.optimizationManager,
      {
        ...this.callbacks,
        onSignalGenerated: (sdp) => this.callbacks.onSignalGenerated(sdp, this.generationForOutgoingSdp(sdp)),
        onIceCandidatesGenerated: (candidates) =>
          this.callbacks.onIceCandidatesGenerated(candidates, this.generationForOutgoingIce()),
        onRemoteStream: (stream) => {
          const rtcPc = connection.getRtcPeerConnection();
          const hasVideo = stream.getVideoTracks().length > 0;
          if (hasVideo) {
            this.attachEncodedFrameTap(rtcPc);
          }
          this.callbacks.onRemoteStream(stream);
        },
      },
      this.logger,
      {
        epoch: this.connectionEpoch,
        connectionEpoch: () => this.connectionEpoch,
        closed: () => this.closed,
        hostCloseInProgress: () => this.hostCloseInProgress,
        isConnected: () => this.connected,
        setConnected: (v) => {
          this.connected = v;
        },
        hasDecodedFrame: () => this._hasDecodedFrame,
        setHasDecodedFrame: (v) => {
          this._hasDecodedFrame = v;
        },
        getPendingCloseReason: () => this.pendingCloseReason,
        setPendingCloseReason: (v) => {
          this.pendingCloseReason = v;
        },
        onHostCloseMessage: (reason) => this.handleHostInitiatedClose(reason),
        onTransportLost: (reason) => this.triggerIceRecovery(reason),
        onIceDisconnected: (conn, isStale) => this.handleIceDisconnected(conn, isStale),
        onIceFailed: (conn, isStale, iceEverChecking, _alreadyRestarted) => {
          if (isStale() || this.closed) return;
          decideAndHandleIceFailure(
            conn,
            this.iceHealth,
            // Failed ICE is a dead PC. An extra in-place restart here is the delay the
            // guest spends staring at a frozen frame before the app refreshes. An early
            // restart during disconnect grace still counts, so we never stack a second.
            { iceEverChecking, alreadyRestarted: true },
            (reason) => {
              // Clear the flag only for the live epoch: a late restartIce() rejection from a
              // torn-down epoch must not clear a newer epoch's guard. triggerIceRecovery()
              // performs the clear once we know this epoch is still current.
              if (isStale() || this.closed) return;
              this.triggerIceRecovery(reason);
            },
            this.logger,
            () => {
              this.iceRestartInFlight = true;
            },
          );
        },
        cancelIceDisconnectGrace: () => this.cancelIceDisconnectGrace(),
        startCongestionMonitoring: (conn) => this.congestion.start(conn),
        setChannelOpenForCongestion: (open) => this.congestion.setChannelOpen(open),
        recordSdpBreadcrumb: (sdp) => this.recordSdpBreadcrumb(sdp),
        waitForCandidatesThenForwardSdp: (conn, sdp, isStale) =>
          this.waitForCandidatesThenForwardSdp(conn, sdp, isStale),
        scheduleDisconnect: (reason) => this.scheduleDisconnect(reason),
        resetRetriesOnConnected: () => {
          // The attempt that just landed is over: stop bounding it.
          this.attemptWatchdogTimer = clearTimer(this.attemptWatchdogTimer);
          this.cancelRetry();
          if (this.reclaimAwaitingMedia) {
            this.silenceWatch.armAwaitingMedia();
            // Only a RECOVERY reclaim (this session already showed video before the drop)
            // gets the active no-stats-sample bound. A connection with no established video
            // baseline holding past GUEST_CONNECTION_STABLE_MS is already a full success —
            // this must not race that refund with a false "no media" reclaim.
            if (this.hadDecodedFrameBeforeReclaim) this.armFirstMediaWatchdog();
          }
          // Refund the attempt budget only once this connection has actually HELD.
          // Zeroing it here — the instant ICE and the data channel came up — let a
          // flapping link retry forever: every momentary success wiped the counter, so
          // the cap never engaged and the guest never reached the refresh that recovers
          // it. A connection that drops again inside the window keeps its attempts spent.
          // Recovered: the next failure starts a fresh deadline rather than inheriting this
          // episode's, which would make it terminal on arrival.
          this.endRecoveryEpisode();
          this.stabilityTimer = clearTimer(this.stabilityTimer);
          this.stabilityTimer = setTimeout(() => {
            this.stabilityTimer = null;
            if (this.closed || !this.connected) return;
            if (this.retryAttempt !== 0) {
              this.logger?.info?.(
                `Connection stable for ${GUEST_CONNECTION_STABLE_MS}ms — attempt budget refunded`,
              );
            }
            this.retryAttempt = 0;
            this.recovery.markMediaResumed();
            this.congestion.getHealthRecorder().recordEvent('transport-resumed', 'signaling');
          }, GUEST_CONNECTION_STABLE_MS);
        },
        afterNegotiation: async () => {
          const rtcPc = connection.getRtcPeerConnection();
          if (rtcPc) await this.mediaPolicyLifecycle.afterNegotiation(rtcPc);
        },
        afterReceiverReplacement: () => {
          const rtcPc = connection.getRtcPeerConnection();
          return rtcPc ? this.mediaPolicyLifecycle.afterReceiverReplacement(rtcPc) : Promise.resolve();
        },
        cancelRetry: () => this.cancelRetry(),
        isIceRestartInFlight: () => this.iceRestartInFlight,
        publishKeyframeRearm: (rearmAndRequest) => {
          this.rearmKeyframes = rearmAndRequest;
        },
          handleLatencyProbeMessage: (data) => this.handleLatencyProbeMessage(data),
        handleInputPhotonMessage: (data) => this.handleInputPhotonMessage(data),
        handleDrainRequest: (data) => this.handleDrainRequest(data),
        handleRecoverPath: (data) => this.handleRecoverPathMessage(data),
      },
    );
  }

  private scheduleDisconnect(reason: string): void {
    // The episode is measured from the LOSS, not from the first reclaim: the debounce, the
    // retry backoff and every pre-offer wait are all time the player spends on a frozen
    // frame, and a budget that skipped them would describe something nobody experiences.
    this.startRecoveryEpisode();
    this.cancelDisconnectTimer();
    // Bind to the current epoch: a debounce armed by the old connection must not
    // fire a second reclaim after a reconnect has already swapped in a new PC.
    const epochAtSchedule = this.connectionEpoch;
    this.disconnectTimer = setTimeout(() => {
      this.disconnectTimer = null;
      if (this.closed) return;
      if (epochAtSchedule !== this.connectionEpoch) return;
      this.pendingCloseReason = null;
      this.notifyDisconnectOrRetry(reason);
    }, 150);
  }

  private waitForCandidatesThenForwardSdp(
    connection: IPeerConnection,
    initialSdp: RTCSessionDescriptionInit,
    isStale: () => boolean,
  ): void {
    const rtcPc = connection.getRtcPeerConnection();
    if (!rtcPc) {
      this.callbacks.onSignalGenerated(initialSdp, this.generationForOutgoingSdp(initialSdp));
      return;
    }
    this.cancelGatherBuffer();
    this.gatherBuffer = new SdpGatherBuffer(GuestPeerSession.ICE_GATHER_TIMEOUT_MS);
    this.gatherBuffer.buffer(rtcPc, initialSdp).then((fullSdp) => {
      if (isStale()) return;
      const count = (fullSdp.sdp?.match(/a=candidate:/g) || []).length;
      this.logger?.info?.(`Forwarding SDP answer, candidates baked in: ${count}`);
      this.callbacks.onSignalGenerated(fullSdp, this.generationForOutgoingSdp(fullSdp));
    });
  }

  private triggerIceRecovery(reason: string): void {
    this.startRecoveryEpisode();
    this.recovery.markTransportLost();
    this.congestion.getHealthRecorder().recordEvent('transport-lost', 'signaling', { reason });
    this.connected = false;
    // An in-place restart that reached recovery has demonstrably failed to heal the
    // transport, so release the guard that suppresses the coordinated reclaim. Leaving it
    // set would make maybeRetry() early-return here — with media already torn down and no
    // retry armed, the guest would sit on the reconnecting spinner until the browser happens
    // to escalate ICE to `failed`.
    this.iceRestartInFlight = false;
    this.cancelDisconnectTimer();
    this.cancelIceDisconnectGrace();
    this.callbacks.onTransportLost?.(reason);
    this.notifyDisconnectOrRetry(reason);
  }

  /**
   * Video stopped, or the network changed, while ICE still reports connected.
   * Restart this peer connection in place. A selected pair with no new frames is not a
   * live path, and dropping the quality tier would not bring the picture back.
   */
  noteNetworkChange(): void {
    if (this.closed || !this.connected || !this._hasDecodedFrame) return;
    this.recoverFromPathDeath('network_change');
  }

  /**
   * Fed by the stats poll. `nowMs` is injectable so tests can cross the silence window.
   * `sourceId` is the stream identity from the sample; a change or a counter decrease
   * re-baselines the watch rather than reading as silence (see GuestPathSilenceWatch).
   */
  noteInboundVideo(
    framesDecoded: number,
    nowMs = Date.now(),
    sourceId?: string,
  ): void {
    const verdict = sourceId !== undefined
      ? this.silenceWatch.noteFrames({ sourceId, framesDecoded, nowMs })
      : this.silenceWatch.noteFrames(framesDecoded, nowMs);
    if (verdict === 'healthy') {
      if (this.pathRestartPending) {
        // After ICE restart, "recent" progress can still be the PRE-restart counter. Clear
        // only when decoded frames have advanced past the arm baseline.
        const baseline = this.pathRestartFramesAtArm;
        if (baseline !== null && framesDecoded > baseline) {
          this.pathRebuildTimer = clearTimer(this.pathRebuildTimer);
          this.pathRestartPending = false;
          this.pathRestartFramesAtArm = null;
          this.silenceWatch.releaseGapArm();
        }
      } else if (this.silenceWatch.hasRecentProgress(nowMs)) {
        this.pathRebuildTimer = clearTimer(this.pathRebuildTimer);
        this.silenceWatch.releaseGapArm();
      }
      if (this.reclaimAwaitingMedia && framesDecoded > 0) {
        this.reclaimAwaitingMedia = false;
        this.firstMediaWatchdogTimer = clearTimer(this.firstMediaWatchdogTimer);
      }
      // Arm the gap timer once per stall, not on every healthy tick: re-arming each second
      // meant one fire could never be followed by a second verdict.
      if (this.silenceWatch.hasSeenProgress() && this.silenceWatch.claimGapArm()) {
        this.armPathSilenceGap();
      }
      return;
    }
    this.pathSilenceGapTimer = clearTimer(this.pathSilenceGapTimer);
    if (verdict === 'no-media') {
      if (!this.reclaimAwaitingMedia) return;
      this.reclaimAwaitingMedia = false;
      this.firstMediaWatchdogTimer = clearTimer(this.firstMediaWatchdogTimer);
      this.triggerIceRecovery('inbound_video_silent');
      return;
    }
    this.recoverFromPathDeath('inbound_video_silent');
  }

  private handleRecoverPathMessage(data: string): boolean {
    if (!isRecoverPathMessage(data)) return false;
    this.noteNetworkChange();
    return true;
  }

  private watchNetworkChanges(): void {
    const target = globalThis.window;
    if (!target?.addEventListener) return;
    const onChange = () => this.noteNetworkChange();
    target.addEventListener('online', onChange);
    const network = (globalThis.navigator as Navigator & { connection?: EventTarget } | undefined)?.connection;
    network?.addEventListener?.('change', onChange);
    this.detachNetworkWatch = () => {
      target.removeEventListener('online', onChange);
      network?.removeEventListener?.('change', onChange);
    };
  }

  private armPathSilenceGap(): void {
    this.pathSilenceGapTimer = clearTimer(this.pathSilenceGapTimer);
    this.pathSilenceGapTimer = setTimeout(() => {
      this.pathSilenceGapTimer = null;
      if (this.closed || !this.connected) return;
      if (this.silenceWatch.notePollGap(Date.now()) === 'silent') {
        this.recoverFromPathDeath('inbound_video_silent');
      }
    }, GUEST_PATH_SILENCE_MS);
  }

  private recoverFromPathDeath(reason: string): void {
    if (this.closed || this.hostCloseInProgress || !this.connected) return;
    // The guard must include iceRestartInFlight: the path-silence restart used to stack on the
    // early-disconnect restart's in-flight restartIce() on the same RTCPeerConnection, and the
    // "one in-flight transport action per session" arbiter was never consulted.
    if (this.pathRestartPending || this.reconnectInFlight || this.iceRestartInFlight) return;
    const connection = this.connection;
    if (!connection) return;
    // Route through the arbiter-guarded wrapper rather than calling connection.restartIce()
    // directly, so this cannot stack on an in-flight reclaim or ICE-grace restart.
    // NOTE: the arbiter action is deliberately NOT finished here — as with restartIce(), it
    // stays owned until the recovery episode resolves or the grace timer cancels it, so this
    // restart remains visible to isReconnectInProgress() and to other transport actions.
    if (!this.beginTransportAction()) return;
    this.pathRestartPending = true;
    const framesAtArm = this.silenceWatch.lastFramesDecoded() ?? 0;
    this.pathRestartFramesAtArm = framesAtArm;
    this.logger?.warn?.(`Inbound video stopped (${reason}) — restarting ICE while the link still looks connected`);
    connection.restartIce().catch((err) => {
      this.logger?.warn?.('Path-silence ICE restart failed', { error: String(err) });
    });
    this.pathRebuildTimer = clearTimer(this.pathRebuildTimer);
    const epoch = this.connectionEpoch;
    this.pathRebuildTimer = setTimeout(() => {
      this.pathRebuildTimer = null;
      this.pathRestartPending = false;
      this.pathRestartFramesAtArm = null;
      if (this.closed || epoch !== this.connectionEpoch) return;
      // Require a frame advance past the arm baseline — not merely "recent" pre-restart progress.
      const last = this.silenceWatch.lastFramesDecoded();
      if (last !== null && last > framesAtArm) return;
      this.triggerIceRecovery(reason);
    }, GUEST_PATH_SILENCE_MS);
  }

  /**
   * If a reclaim connects but getStats never delivers a sample, noteInboundVideo never runs
   * and the silence watch cannot fire `no-media`. Bound that hang here.
   */
  private armFirstMediaWatchdog(): void {
    this.firstMediaWatchdogTimer = clearTimer(this.firstMediaWatchdogTimer);
    const epoch = this.connectionEpoch;
    this.firstMediaWatchdogTimer = setTimeout(() => {
      this.firstMediaWatchdogTimer = null;
      if (this.closed || epoch !== this.connectionEpoch) return;
      if (!this.reclaimAwaitingMedia || !this.connected) return;
      this.reclaimAwaitingMedia = false;
      this.triggerIceRecovery('inbound_video_silent');
    }, GUEST_PATH_SILENCE_MS);
  }

  private handleIceDisconnected(connection: IPeerConnection, isStale: () => boolean): void {
    if (this.hostCloseInProgress || isStale() || this.closed) return;
    if (!this.connected) return;

    this.logger?.warn?.('ICE disconnected — starting grace period');
    this.logger?.breadcrumb?.('ICE disconnected, grace period started', 'warning');

    this.cancelIceDisconnectGrace();
    this.iceDisconnectGraceTimer = setTimeout(() => {
      this.iceDisconnectGraceTimer = null;
      if (isStale() || this.closed || this.hostCloseInProgress) return;

      const rtc = connection.getRtcPeerConnection();
      const currentIce = rtc?.iceConnectionState;
      if (currentIce === 'connected' || currentIce === 'completed') return;

      this.logger?.warn?.('ICE did not recover within grace period');
      this.logger?.breadcrumb?.(`ICE grace expired, state: ${currentIce}`, 'error');
      // Only now is the inbound media known to be dead. Clearing it the instant ICE
      // blinks would blank the video and raise the reconnect cover for every transient
      // packet-loss spike that recovers well inside the grace window.
      this.callbacks.onIceDisconnected?.();
      this.pendingCloseReason = 'ice_disconnected';
      this.triggerIceRecovery('ice_disconnected');
    }, GuestPeerSession.ICE_DISCONNECT_GRACE_MS);

    this.iceEarlyRestartTimer = setTimeout(() => {
      this.iceEarlyRestartTimer = null;
      if (isStale() || this.closed || this.hostCloseInProgress) return;
      // A restart from the ICE-failed path already owns this recovery.
      if (this.iceRestartInFlight) return;
      // Only a still-disconnected transport is restarted: connected/completed means the
      // blip self-healed, and checking/failed have their own paths.
      if (connection.getRtcPeerConnection()?.iceConnectionState !== 'disconnected') return;
      if (!this.beginTransportAction()) return;

      this.logger?.info?.('ICE still disconnected — attempting early in-place restart');
      this.iceRestartInFlight = true;
      connection.restartIce().catch((err) => {
        // A rejected restart is a local PC error, not proof the transport is dead — the
        // grace timer still owns the fallthrough to a full reclaim.
        this.logger?.warn?.('Early ICE restart failed', { error: String(err) });
        if (!isStale()) this.iceRestartInFlight = false;
      });
    }, GuestPeerSession.ICE_EARLY_RESTART_MS);
  }

  private handleHostInitiatedClose(reason: string): void {
    this.connected = false;
    this.cancelRetry();
    this.cancelAttemptTimers();
    this.endRecoveryEpisode();
    this.cancelDisconnectTimer();
    this.connectionEpoch++;
    this.hostCloseInProgress = true;
    this.destroyConnection();
    this.hostCloseInProgress = false;
    this.pendingCloseReason = null;
    this.callbacks.onDisconnected(reason);
  }

  private maybeRetry(reason: string): void {
    if (!this.willRetry(reason) || !this.retryPolicy) return;
    if (!this.lastHostPeerId || !this.lastIceServers || !this.lastMetadata) return;
    // Exactly one coordinated reclaim per failover: don't stack a second timer
    // while one is already pending or a reconnect is mid-flight.
    if (this.retryTimer !== null || this.reconnectInFlight || this.iceRestartInFlight) return;

    // The deadline runs from the start of an episode, not from each attempt. Normally the
    // transport-loss path has already started it; this is the belt-and-braces case where a
    // retry is armed from somewhere that never saw a loss event.
    this.startRecoveryEpisode();

    this.retryAttempt++;
    const delay = this.retryPolicy.retryDelayMs(this.retryAttempt);
    this.callbacks.onRetrying(this.retryAttempt, reason);

    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.runReconnect(reason);
    }, delay);
  }

  /**
   * Coordinated cellular-failover reconnect (single reclaim per failover):
   *   1. await the realtime-websocket-ready gate so the re-offer is only issued
   *      once the WS is re-established on the surviving (cellular) path,
   *   2. await the optional reclaim/ICE refresh (onBeforeReconnect),
   *   3. rebuild the PC with a relay-forced transport policy for the new NAT path.
   */
  private async runReconnect(reason: string): Promise<void> {
    if (this.closed || this.reconnectInFlight) return;
    if (!this.lastHostPeerId || !this.lastIceServers || !this.lastMetadata) return;
    if (!this.beginTransportAction()) {
      // A Player refresh already owns the rebuild. Retry arbitration without spending another attempt.
      this.retryTimer = setTimeout(() => { this.retryTimer = null; void this.runReconnect(reason); }, 250);
      return;
    }

    this.reconnectInFlight = true;
    let failure: { error: unknown } | null = null;
    try {
      if (this.realtimeReadyGate) {
        try {
          // Bounded by what is LEFT of the recovery budget, not by the gate's own patience.
          // This wait happens before an offer exists, so the attempt watchdog cannot see it
          // — the gate can legitimately wait tens of seconds for a WebSocket to come back on
          // a cellular failover, and an unbounded wait here made the recovery ceiling
          // nominal: the guest sat silent, past the host's hold, with nothing timing it.
          await this.withinRecoveryBudget(this.realtimeReadyGate(), 'realtime-ready gate');
        } catch (err) {
          this.logger?.warn?.('realtime-ready gate rejected; reconnecting anyway', {
            error: String(err),
          });
        }
      }
      if (this.closed) return;
      // The budget can have run out while waiting above; a reclaim issued now would be
      // answering for a session the host has already let go.
      if (this.recoveryCeilingReached()) {
        this.logger?.warn?.('Recovery budget spent before the re-offer — refreshing instead');
        this.connected = false;
        this.notifyDisconnectOrRetry(reason);
        return;
      }
      if (!this.lastHostPeerId || !this.lastIceServers || !this.lastMetadata) return;

      let iceServers = this.lastIceServers;
      let relay = this.reconnectRelay;
      if (this.callbacks.onBeforeReconnect) {
        try {
          const result = await this.withinRecoveryBudget(
            this.callbacks.onBeforeReconnect({
              attempt: this.retryAttempt,
              reason,
              hostPeerId: this.lastHostPeerId,
              iceServers: this.lastIceServers,
              metadata: this.lastMetadata,
            }),
            'onBeforeReconnect',
          );
          if (result?.iceServers?.length) {
            iceServers = result.iceServers;
            this.lastIceServers = iceServers;
          }
          if (result?.relay) relay = result.relay;
        } catch (err) {
          this.logger?.warn?.('onBeforeReconnect failed; reconnecting with cached ICE', {
            error: String(err),
          });
          // The budget may have run out while onBeforeReconnect was in flight; a reclaim
          // issued now would answer for a session the host has already let go.
          if (this.recoveryCeilingReached()) {
            this.logger?.warn?.('Recovery budget spent after onBeforeReconnect — refreshing instead');
            this.connected = false;
            this.notifyDisconnectOrRetry(reason);
            return;
          }
        }
      }

      if (this.closed) return;
      // An in-place ICE restart may have recovered the session while the reclaim was in
      // flight; rebuilding now would tear down a healthy connection for nothing.
      if (this.connected) {
        this.logger?.info?.('Connection recovered during reconnect prep — skipping rebuild');
        return;
      }
      if (!this.lastHostPeerId || !this.lastMetadata) return;
      const connectOptions: GuestConnectOptions | undefined = relay.forceRelay
        ? { transportPolicy: relay.transportPolicy }
        : undefined;
      try {
        this.reclaimAwaitingMedia = true;
        // Snapshot BEFORE connect() resets _hasDecodedFrame — this is what lets the
        // post-connect watchdog tell a recovery reclaim from an initial connection.
        this.hadDecodedFrameBeforeReclaim = this._hasDecodedFrame;
        await this.connect(this.lastHostPeerId, iceServers, this.lastMetadata, connectOptions);
        this.armAttemptWatchdog();
      } catch (error) {
        failure = { error };
      }
    } finally {
      this.reconnectInFlight = false;
    }

    if (failure) this.handleReconnectFailure(reason, failure.error);
  }

  /**
   * Bound one reclaim attempt.
   *
   * connect() resolves as soon as the offer is issued — it does not await the transport
   * coming up, and success is signalled only by later ICE/channel events. A rebuilt PC that
   * sits in `new`/`checking` therefore emits nothing at all: the ICE-disconnect grace does
   * not apply (it requires a connection that was previously up) and the retry ladder never
   * re-arms, so the guest holds the reconnecting spinner until the browser gets around to
   * declaring ICE failed. This timer makes the attempt fail on our schedule instead.
   */
  private armAttemptWatchdog(): void {
    this.attemptWatchdogTimer = clearTimer(this.attemptWatchdogTimer);
    const epoch = this.connectionEpoch;
    this.attemptWatchdogTimer = setTimeout(() => {
      this.attemptWatchdogTimer = null;
      if (this.closed || this.connected) return;
      // A newer connect() owns the session now; this attempt is no longer the live one.
      if (epoch !== this.connectionEpoch) return;

      // An attempt that is actively negotiating is NOT a dead attempt, and cutting it short
      // cannot help: the refresh waiting behind it would have to redo the very same relay
      // negotiation from scratch, so abandoning a live one only guarantees a LATER picture.
      // Let it run - bounded by the absolute ceiling, not by the no-progress deadline.
      if (this.iceIsProgressing()) {
        if (this.recoveryCeilingReached()) {
          this.logger?.warn?.(
            `Reclaim still negotiating at the ${GUEST_RECOVERY_HARD_CEILING_MS}ms ceiling — ` +
              'past the host reconnect hold, so refreshing instead',
          );
          this.notifyDisconnectOrRetry('ice_timeout');
          return;
        }
        this.logger?.info?.('Reclaim attempt still negotiating — extending');
        this.armAttemptWatchdog();
        return;
      }

      // Going nowhere, and out of budget: hand over to the refresh.
      if (this.recoveryDeadlineExpired()) {
        this.logger?.warn?.(
          `No ICE progress by the ${GUEST_RECOVERY_DEADLINE_MS}ms deadline — giving up on reclaim`,
        );
        this.notifyDisconnectOrRetry('ice_timeout');
        return;
      }

      this.logger?.warn?.(
        `Reclaim attempt ${this.retryAttempt} made no ICE progress in ` +
          `${GUEST_RECONNECT_ATTEMPT_TIMEOUT_MS}ms — failing it`,
      );
      this.notifyDisconnectOrRetry('ice_timeout');
    }, this.attemptWatchdogDelayMs());
  }

  /**
   * How long the next watchdog tick may wait: the per-attempt bound, or whatever is left of
   * the EPISODE if that is shorter.
   *
   * Without the second half the ceiling was only observed on a one-second grid, so a "10s
   * cap" actually gave up somewhere in the eleventh second. The cap is a promise to the
   * player; it should not be rounded up by an implementation detail of the poll.
   */
  private attemptWatchdogDelayMs(): number {
    return Math.min(GUEST_RECONNECT_ATTEMPT_TIMEOUT_MS, this.recoveryBudgetRemainingMs());
  }

  /**
   * Race a pre-offer wait against what remains of the recovery budget.
   *
   * These waits sit before any offer exists, so the attempt watchdog cannot bound them, and
   * each one is free to wait far longer than the session has left. Rejecting when the budget
   * is gone lets the caller fall through to the refresh instead of blocking on a promise
   * nobody is waiting for any more.
   */
  private withinRecoveryBudget<T>(work: Promise<T>, label: string): Promise<T> {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let settled = false;
    const budget = new Promise<never>((_resolve, reject) => {
      const arm = (): void => {
        const remaining = this.recoveryBudgetRemainingMs();
        timer = setTimeout(() => {
          if (settled) return;
          // The timer is wall-clock; the budget is not. Time the document spent hidden did
          // not spend any of it, so a timer that comes due against a suspended clock has
          // measured the background gap, not the player's patience — re-arm on what is
          // actually left. With the document visible this re-check always finds zero, so
          // the visible-path timing is byte-for-byte what it was.
          if (this.recoveryBudgetRemainingMs() > 0) {
            arm();
            return;
          }
          reject(new Error(`${label} exceeded the recovery budget (${remaining}ms left)`));
        }, remaining);
      };
      arm();
    });
    return Promise.race([work, budget]).finally(() => {
      settled = true;
      if (timer) clearTimeout(timer);
    }) as Promise<T>;
  }

  /** True when ICE has moved off `new`, i.e. the attempt is actually negotiating. */
  private iceIsProgressing(): boolean {
    const state = this.connection?.getRtcPeerConnection()?.iceConnectionState;
    return state === 'checking' || state === 'connected' || state === 'completed';
  }

  /**
   * How long the current recovery episode has been running; 0 when not recovering.
   * Hidden time is excluded — a backgrounded tab is not a waiting user.
   */
  private recoveryElapsedMs(): number {
    return this.recovery.elapsedMs();
  }

  /** True once a recovery that is making no progress has run past what a player will wait. */
  private recoveryDeadlineExpired(): boolean {
    return this.recovery.deadlineExpired();
  }

  /** True once the episode has spent its whole budget, however healthy it still looks. */
  private recoveryCeilingReached(): boolean {
    return this.recovery.ceilingReached();
  }

  /** Begin the episode if one is not already running. */
  private startRecoveryEpisode(): void {
    if (this.closed) return;
    this.recovery.start();
  }

  /**
   * The document came back after longer than the session can survive hidden.
   *
   * Reported as terminal with a RETRYABLE reason on purpose: in-place reclaim is hopeless
   * (the host let the seat go minutes ago) but the player is right here and wants to play,
   * so the app-level handler rejoins cleanly instead of showing a dead end. The episode is
   * reset first — the rejoin is a fresh start and deserves a full budget.
   */
  private handleHiddenSessionExpired(): void {
    if (this.closed) return;
    this.connected = false;
    this.cancelRetry();
    this.cancelAttemptTimers();
    this.cancelDisconnectTimer();
    this.cancelIceDisconnectGrace();
    this.endRecoveryEpisode();
    this.callbacks.onDisconnected(HIDDEN_SESSION_EXPIRED_REASON);
  }

  /**
   * connect() tears the previous peer connection down before it can throw, so a failed
   * rebuild leaves nothing behind that could ever emit 'disconnected'. Since the retry
   * chain is driven by those events, the session would sit in "reconnecting" forever —
   * re-arm the next attempt (or go terminal) explicitly.
   */
  private handleReconnectFailure(reason: string, error: unknown): void {
    this.logger?.error?.('Reconnect failed while rebuilding the peer connection', {
      error: String(error),
    });
    if (this.closed) return;
    this.connected = false;
    this.notifyDisconnectOrRetry(reason);
  }

  /** True when a transient failure can still be retried under the configured policy. */
  private willRetry(reason: string): boolean {
    if (this.closed) return false;
    if (!this.retryPolicy) return false;
    // Out of budget with nothing advancing: stop reclaiming so the app can refresh. An
    // attempt still negotiating is exempt until the absolute ceiling — see armAttemptWatchdog.
    if (this.recoveryDeadlineExpired() && !this.iceIsProgressing()) return false;
    if (this.recoveryCeilingReached()) return false;
    if (isDeliberateSessionEnd(reason) || !isDisconnectRetryable(reason)) return false;
    if (this.retryAttempt >= this.retryPolicy.maxRetries) return false;
    if (!this.retryPolicy.shouldRetry(reason, this.retryAttempt)) return false;
    if (!this.lastHostPeerId || !this.lastIceServers || !this.lastMetadata) return false;
    return true;
  }

  /**
   * Either start a PeerCore-owned transient reconnect or emit a terminal disconnect.
   * Apps should clear media on both onRetrying and onDisconnected.
   */
  private notifyDisconnectOrRetry(reason: string): void {
    if (this.willRetry(reason)) {
      this.maybeRetry(reason);
      return;
    }
    // Terminal: no attempt is in flight any more, so nothing may still be bounding one.
    // A watchdog left armed here reports the session as still reconnecting and holds the
    // app on its recovering UI after it has been told the session ended.
    //
    // The EPISODE deliberately survives this. Handing over to the app's refresh is the
    // moment recoveryBudgetRemainingMs() has to tell the truth about what is left, and
    // clearing it here is precisely how the ~33s stack was built: each layer looked at a
    // fresh budget and none of them was wrong on its own. A refresh that means to spend a
    // second budget says so, through resetRecoveryEpisode() or a fresh connect().
    this.cancelAttemptTimers();
    this.callbacks.onDisconnected(reason);
  }

  private recordSdpBreadcrumb(sdp: RTCSessionDescriptionInit): void {
    recordGuestSdpBreadcrumb(sdp, this.sdpInspector, this.lastHostPeerId, this.logger);
  }

  private logRemoteCandidateCount(connection: IPeerConnection): void {
    logRemoteCandidateCounts(connection, this.iceHealth, this.logger);
  }

  private isPreWarmHealthy(): boolean {
    if (!this.preWarmedConnection) return false;
    const result = this.iceHealth.checkPreWarm(
      this.preWarmedConnection,
      this.preWarmCreatedAt,
      GuestPeerSession.PREWARM_MAX_AGE_MS,
    );
    if (!result.healthy) {
      this.logger?.warn?.(`Pre-warm unhealthy: ${result.reason}`);
    }
    return result.healthy;
  }

  private drainPendingRemoteIceCandidates(): void {
    const connection = this.connection;
    if (!connection || this.pendingRemoteIceCandidates.length === 0) return;
    const pending = this.pendingRemoteIceCandidates;
    this.pendingRemoteIceCandidates = [];
    this.logger?.info?.(`Draining ${pending.length} buffered remote ICE candidate(s)`);
    for (const candidate of pending) {
      connection.addIceCandidate(candidate).catch((err) => {
        this.logger?.warn?.('Failed to add buffered remote ICE candidate', {
          error: String(err),
        });
      });
    }
  }

  private cancelRetry(): void {
    this.retryTimer = clearTimer(this.retryTimer);
    this.iceRestartInFlight = false;
  }

  /** Timers that bound or refund a reclaim attempt; both die with the session. */
  private cancelAttemptTimers(): void {
    this.attemptWatchdogTimer = clearTimer(this.attemptWatchdogTimer);
    this.stabilityTimer = clearTimer(this.stabilityTimer);
    this.pathSilenceGapTimer = clearTimer(this.pathSilenceGapTimer);
    this.pathRebuildTimer = clearTimer(this.pathRebuildTimer);
    this.firstMediaWatchdogTimer = clearTimer(this.firstMediaWatchdogTimer);
  }

  /**
   * End the current recovery episode, so the next failure is timed from its own start.
   *
   * Deliberately NOT part of cancelAttemptTimers: connect() cancels those timers on every
   * rebuild, and a reclaim rebuilds by definition. Clearing the clock there restarted the
   * episode on each attempt, which made the elapsed time permanently near zero and the
   * ceiling unreachable — the ladder could run forever while looking bounded.
   */
  private endRecoveryEpisode(): void {
    this.recovery.end();
    if (this.transportActionId) this.recoveryActionArbiter.finish(this.transportActionId, 'recovered');
    this.transportActionId = undefined;
  }

  /** Transport and Player refresh share one owner across component renders. */
  private beginTransportAction(): boolean {
    if (this.transportActionId) return true;
    const id = `transport:${this.connectionEpoch}:${++this.transportActionSequence}`;
    if (!this.recoveryActionArbiter.tryBegin(id, 'transport')) return false;
    this.transportActionId = id;
    return true;
  }

  private cancelDisconnectTimer(): void {
    this.disconnectTimer = clearTimer(this.disconnectTimer);
  }

  private cancelIceDisconnectGrace(): void {
    this.iceDisconnectGraceTimer = clearTimer(this.iceDisconnectGraceTimer);
    this.iceEarlyRestartTimer = clearTimer(this.iceEarlyRestartTimer);
    const ice = this.connection?.getRtcPeerConnection()?.iceConnectionState;
    if (ice === 'connected' || ice === 'completed') {
      if (this.transportActionId) this.recoveryActionArbiter.finish(this.transportActionId, 'recovered');
      this.transportActionId = undefined;
    }
  }

  private cancelGatherBuffer(): void {
    this.gatherBuffer?.cancel();
    this.gatherBuffer = null;
  }

  private unwireEpoch(): void {
    this.unwiredEpoch?.();
    this.unwiredEpoch = null;
  }

  private destroyConnection(): void {
    // markClosed stops the poll AND latches the monitor shut, so an in-flight getStats()
    // resolving after teardown cannot send on a dead channel.
    this.congestion.markClosed();
    this.receiverHints.attach(null);
    this.encodedFrameTap?.detach();
    this.rearmKeyframes = null;
    this.unwireEpoch();
    if (this.connection) {
      this.connection.destroy();
      this.connection = null;
    }
  }

  /** Hand the connection's video receiver to the app's encoded-frame tap, if it has one. */
  private attachEncodedFrameTap(rtcPc: RTCPeerConnection | null | undefined): void {
    if (!this.encodedFrameTap || !rtcPc || typeof rtcPc.getReceivers !== 'function') return;
    const video = rtcPc.getReceivers().find((receiver) => receiver.track?.kind === 'video');
    if (!video) return;
    try {
      this.encodedFrameTap.attach(video);
    } catch (err) {
      this.logger?.warn?.('Encoded-frame tap attach failed', { error: String(err) });
    }
  }

  private destroyPreWarmedConnection(): void {
    if (this.preWarmedConnection) {
      this.preWarmedConnection.destroy();
      this.preWarmedConnection = null;
    }
  }
}
